import {useCallback, useEffect, useMemo, useState} from "react";
import {useOutletContext, useSearchParams} from "react-router";
import {useMediaQuery, useTheme} from "@mui/material";
import Grid from "@mui/material/Grid2";
import {apiGet} from "../utils";
import {buildV2QueryParams} from "../utils/filters";
import {LoadingPanel} from "../utils/loading_spinner";
import {RfmSegmentGrid} from "../components/RfmSegmentGrid";
import {RfmBubbleChart} from "../components/RfmBubbleChart";
import {CustomerHealthSignalsTable} from "../components/CustomerHealthSignalsTable";
import {CustomerHealthFilterBar, ALL_RISK_TIER_VALUES} from "../components/CustomerHealthFilterBar";

const RFM_ROW_CARD_HEIGHT = 520;

export const CustomerHealthSignalsContainer = () => {
    const theme = useTheme();
    const isMdUp = useMediaQuery(theme.breakpoints.up('md'));
    const {filters, setFilters} = useOutletContext();
    const [searchParams] = useSearchParams();

    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [selectedSegment, setSelectedSegment] = useState(null);
    const [searchText, setSearchText] = useState('');
    // Default = every tier checked (shows every account). A chat "Show All ->"
    // link narrows this to the specific tier(s) it answered about.
    const [riskTiers, setRiskTiers] = useState(ALL_RISK_TIER_VALUES);
    // "Buying less" (ch2_buying_less) is an OR-of-two-signals rule, not a risk
    // tier - no persistent UI control for it, only reachable via the chat
    // deep link, surfaced as a dismissible chip in the filter bar.
    const [buyingLessOnly, setBuyingLessOnly] = useState(false);

    // Re-applies every time the URL's query params change (not just on first
    // mount) - a chat "Show All ->" link can arrive while this page is
    // already open, which updates search params without remounting this
    // container, so a mount-only read would silently miss it.
    useEffect(() => {
        const riskTierParam = searchParams.get('riskTier');
        setRiskTiers(riskTierParam ? riskTierParam.split(',').filter(Boolean) : ALL_RISK_TIER_VALUES);
        setBuyingLessOnly(searchParams.get('preset') === 'buying_less');

        // A "Show All ->" link carries the exact current/prior window the
        // chat answer used - sync the filter bar to it so the table shows
        // the same comparison. With no window in the URL (a plain visit to
        // this page), leave the filter bar alone - it already defaults to
        // the same YTD-vs-prior-YTD as every other page (buildDefaultDateFilters)
        // - confirmed 2026-10-31, now that a chat link is the way users get
        // here with a specific window in mind, not a page-specific default.
        const currentStart = searchParams.get('currentStart');
        const currentEnd = searchParams.get('currentEnd');
        const priorStart = searchParams.get('priorStart');
        const priorEnd = searchParams.get('priorEnd');
        if (currentStart && currentEnd && priorStart && priorEnd) {
            setFilters((prev) => ({
                ...prev,
                currentPeriodStart: currentStart,
                currentPeriodEnd: currentEnd,
                priorPeriodStart: priorStart,
                priorPeriodEnd: priorEnd,
            }));
        }
    }, [searchParams, setFilters]);

    const sortKey = searchParams.get('sort') || undefined;
    const sortDir = searchParams.get('dir') || undefined;

    const fetchCustomerHealth = useCallback(() => {
        setLoading(true);
        apiGet(`v2/customer-health${buildV2QueryParams(filters)}`, (resp) => {
            if (resp.success && resp.data) {
                setData(resp.data);
            } else {
                console.error('Customer Health API error:', resp.error);
                setData({breakdown: [], segmentCounts: {}});
            }
            setLoading(false);
        });
    }, [filters]);

    useEffect(() => {
        fetchCustomerHealth();
    }, [fetchCustomerHealth]);

    const filteredBreakdown = useMemo(() => {
        let rows = data?.breakdown || [];
        if (selectedSegment) {
            rows = rows.filter((row) => row.segment === selectedSegment);
        }
        rows = rows.filter((row) => riskTiers.includes(row.risk_tier));
        if (buyingLessOnly) {
            // Mirrors ch2_buying_less (agent.customer-health.db.js::assembleBuyingLess):
            // not lost, and order frequency down 25%+ or net revenue down 35%+ vs. the
            // prior-year window. delta_pct here is signed (negative = decline), so the
            // decline-% thresholds there become "delta_pct <= -25/-35" here.
            rows = rows.filter((row) => (
                row.risk_tier !== 'lost'
                && ((row.orders.delta_pct != null && row.orders.delta_pct <= -25)
                    || (row.net_revenue.delta_pct != null && row.net_revenue.delta_pct <= -35))
            ));
        }
        const search = searchText.trim().toLowerCase();
        if (search) {
            rows = rows.filter((row) => (
                String(row.name || '').toLowerCase().includes(search)
                || String(row.number || '').toLowerCase().includes(search)
                || String(row.parent_account_name || '').toLowerCase().includes(search)
            ));
        }
        return rows;
    }, [data?.breakdown, selectedSegment, riskTiers, buyingLessOnly, searchText]);

    // RfmBubbleChart expects plain net_revenue/variable_margin/avg_order_value
    // numbers (the L12m RFM figures) - this table's own fields of the same
    // name are {current, prior, delta, ...} signal objects, so map to the
    // chart's shape here rather than changing the shared chart component.
    const rfmCustomers = useMemo(() => (data?.breakdown || []).map((row) => ({
        name: row.name,
        segment: row.segment,
        days_since_last_order: row.days_since_last_order,
        number_of_orders: row.number_of_orders,
        net_revenue: row.net_revenue_l12,
        variable_margin: row.variable_margin_l12,
        avg_order_value: row.avg_order_value_l12,
        recency_score: row.recency_score,
        frequency_score: row.frequency_score,
    })), [data?.breakdown]);

    if (loading) {
        return <LoadingPanel loading={true} message="Loading customer health..."/>;
    }

    return (
        <div style={{marginTop: 30, marginBottom: 100, marginLeft: 30, marginRight: 30}}>
            <Grid container spacing={2} style={{marginBottom: 16}} alignItems="stretch">
                <Grid size={{xs: 12, md: 4}} sx={{maxWidth: {md: 450}, width: '100%', display: 'flex', minHeight: 0}}>
                    <RfmSegmentGrid
                        segmentCounts={data?.segmentCounts || {}}
                        selectedSegment={selectedSegment}
                        onSelectSegment={setSelectedSegment}
                        compact
                        matrixWidth={360}
                        cardHeight={isMdUp ? RFM_ROW_CARD_HEIGHT : null}
                    />
                </Grid>
                <Grid size={{xs: 12, md: 8}} sx={{minWidth: 0, display: 'flex', minHeight: 0}}>
                    <RfmBubbleChart
                        customers={rfmCustomers}
                        selectedSegment={selectedSegment}
                        cardHeight={isMdUp ? RFM_ROW_CARD_HEIGHT : null}
                    />
                </Grid>
            </Grid>

            <CustomerHealthFilterBar
                searchText={searchText}
                onSearchTextChange={setSearchText}
                riskTiers={riskTiers}
                onRiskTiersChange={setRiskTiers}
                buyingLessOnly={buyingLessOnly}
                onClearBuyingLessOnly={() => setBuyingLessOnly(false)}
            />

            <CustomerHealthSignalsTable
                breakdown={filteredBreakdown}
                initialSortKey={sortKey}
                initialSortDir={sortDir}
            />
        </div>
    );
};
